import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CustomThemeContext } from '../../App';
import { useLocation } from 'react-router-dom';
import { UserIcon, LogoutIcon } from './Icons';
import { useNavigate } from 'react-router-dom';
import { AuthMode, useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { updateMyPassword } from '../../api/user';
import { validatePasswordChange } from '../../utils/passwordChange';
import { getPageLocation } from './navigation';
import { ConsoleInput, ConsoleLabel } from '../../styles/common/input.styled';
import { Button } from '../../styles/common/Button.styled';
import { onAccent, statusColors } from '../../styles/consoleTheme';

const HeaderContainer = styled.header`
  height: 52px; padding: 0 24px; flex-shrink: 0; display: flex; align-items: center; justify-content: space-between; gap: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.color.primary_300}; background: ${({ theme }) => theme.color.primary_100};
`;
const Location = styled.nav`
  display: flex; align-items: center; gap: 10px; min-width: 0; font-size: 12px; color: ${({ theme }) => theme.color.text_color_300};
  span:last-child { color: ${({ theme }) => theme.color.text_color_200}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;
const Actions = styled.div`display: flex; align-items: center; gap: 10px; flex-shrink: 0;`;
const ThemeToggle = styled(Button).attrs({ $variant: 'ghost', $size: 'sm' })``;
const ProfileWrapper = styled.div`position: relative;`;
const UserProfile = styled.button<{ $active?: boolean }>`
  display: flex; align-items: center; gap: 8px; background: transparent; border: 0; border-radius: 6px; padding: 3px 8px;
  color: ${({ theme }) => theme.color.text_color_200}; cursor: pointer;
  &:hover { background: ${({ theme }) => theme.color.primary_50}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.color.point_y_100}; outline-offset: 2px; }
`;
const UserIconWrapper = styled.div`width: 28px; height: 28px; display: grid; place-items: center; border: 1px solid ${({ theme }) => theme.color.primary_300}; border-radius: 50%; background: ${({ theme }) => theme.color.primary_50};`;
const UserInfo = styled.div`display: flex; flex-direction: column; align-items: flex-start; gap: 1px;`;
const UserName = styled.span`font-size: 12px; font-weight: 500;`;
const CompanyName = styled.span`max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 10px; color: ${({ theme }) => theme.color.text_color_300};`;
const LogoutButton = styled(Button).attrs({ $variant: 'ghost', $size: 'sm' })`width: 28px; padding: 0;`;

const ProfilePanel = styled.div`
  position: fixed;
  top: 58px;
  right: 20px;
  width: 360px;
  max-width: calc(100vw - 32px);
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.color.primary_300};
  background-color: ${({ theme }) => theme.color.primary_100};
  color: ${({ theme }) => theme.color.text_color_100};
  box-shadow: ${({ theme }) => theme.name === 'dark'
    ? '0 8px 32px rgba(0, 0, 0, 0.28)'
    : '0 8px 32px rgba(32, 36, 44, 0.12)'};
  z-index: 300;
  max-height: calc(100dvh - 74px);
  overflow-y: auto;
  padding: 14px;
`;

const PanelSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PanelDivider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.color.primary_300};
  margin: 14px 0;
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 13px;
  font-weight: 800;
  color: ${({ theme }) => theme.color.text_color_100};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 86px minmax(0, 1fr);
  gap: 8px 12px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.color.primary_300};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.color.primary_50};
`;

const InfoLabel = styled.span`
  align-self: center;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.text_color_300};
`;

const InfoText = styled.span`
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.text_color_100};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ModuleSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 11px 12px 12px;
  border: 1px solid ${({ theme }) => theme.color.primary_300};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.color.primary_50};
`;

const ModuleSectionTitle = styled.h4`
  margin: 0;
  color: ${({ theme }) => theme.color.text_color_100};
  font-size: 12px;
  font-weight: 800;
`;

const MODE_LABELS: Record<AuthMode, string> = {
  basic: '콜리스 서비스 콘솔',
  core: '통화 전용 콘솔',
};

const ModeValue = styled.span<{ $mode: AuthMode }>`
  display: inline-flex;
  align-items: center;
  min-width: 0;
  width: fit-content;
  max-width: 100%;
  padding: 4px 8px;
  border: 1px solid ${({ theme, $mode }) => $mode === 'basic' ? statusColors[theme.name].success : theme.color.point_y_100};
  border-radius: 6px;
  background-color: ${({ theme, $mode }) => $mode === 'basic' ? statusColors[theme.name].success + '18' : theme.color.point_y_100 + '18'};
  color: ${({ theme, $mode }) => $mode === 'basic' ? statusColors[theme.name].success : theme.color.point_y_100};
  font-size: 11px;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ModeSwitch = styled.div`
  display: flex;
  gap: 3px;
  min-width: 0;
  padding: 3px;
  border: 1px solid ${({ theme }) => theme.color.primary_300};
  border-radius: 7px;
  background-color: ${({ theme }) => theme.color.primary_50};
`;

const ModeButton = styled.button<{ $mode: AuthMode; $active: boolean }>`
  flex: 1;
  min-width: 0;
  height: 30px;
  padding: 0 8px;
  border: 1px solid transparent;
  border-radius: 5px;
  background-color: ${({ theme, $mode, $active }) => $active
    ? $mode === 'basic' ? statusColors[theme.name].success : theme.color.point_y_100
    : 'transparent'};
  color: ${({ theme, $mode, $active }) => $active
    ? onAccent(theme)
    : $mode === 'basic' ? statusColors[theme.name].success : theme.color.point_y_100};
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease;

  &:hover {
    background-color: ${({ theme, $mode, $active }) => $active
      ? $mode === 'basic' ? statusColors[theme.name].success : theme.color.point_y_100
      : $mode === 'basic' ? statusColors[theme.name].success + '18' : theme.color.point_y_100 + '18'};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme, $mode }) => $mode === 'basic' ? statusColors[theme.name].success : theme.color.point_y_100};
    outline-offset: 1px;
  }
`;

const PasswordForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 9px;
`;

const FieldGroup = styled.label`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FieldLabel = ConsoleLabel;
const PasswordInput = ConsoleInput;

const PanelActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 3px;
`;

const SaveButton = Button;

const Header = () => {
  const { theme, onChangeTheme } = useContext(CustomThemeContext);
  const {
    mode,
    user,
    basicUser,
    coreUser,
    basicAccessToken,
    coreAccessToken,
    switchMode,
    logout,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const pageLocation = getPageLocation(location.pathname);
  const profileRef = useRef<HTMLDivElement>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const hasBothConsoleModes = Boolean(basicAccessToken && coreAccessToken && basicUser && coreUser);
  useEffect(() => {
    if (!isProfileOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isProfileOpen]);

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const validationMessage = validatePasswordChange(passwordForm);
    if (validationMessage) {
      toast.error(validationMessage);
      return;
    }

    try {
      setIsChangingPassword(true);
      await updateMyPassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('비밀번호가 변경되었습니다.');
    } catch (error: any) {
      toast.error(error.response?.data?.message || '비밀번호 변경에 실패했습니다.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleModeSwitch = (nextMode: AuthMode) => {
    switchMode(nextMode);
    setIsProfileOpen(false);
    navigate('/statistics/calls');
  };

  return (
    <HeaderContainer>
      <Location aria-label="현재 위치"><span>{pageLocation.group}</span><span aria-hidden="true">/</span><span aria-current="page">{pageLocation.title}</span></Location>
      <Actions>
        <ThemeToggle onClick={onChangeTheme}>
          {theme === 'light' ? '다크 모드' : '라이트 모드'}
        </ThemeToggle>
        <ProfileWrapper ref={profileRef}>
          <UserProfile
            type="button"
            $active={isProfileOpen}
            aria-expanded={isProfileOpen}
            onClick={() => setIsProfileOpen((prev) => !prev)}
          >
            <UserIconWrapper>
              <UserIcon />
            </UserIconWrapper>
            <UserInfo>
              <UserName>{user?.username || '사용자'}</UserName>
              <CompanyName>
                {user?.groupDisplayName || 'Callis'}
                {user?.roleName ? ` · ${user.roleName}` : ''}
                {user?.phone ? ` (${user.phone})` : ''}
              </CompanyName>
            </UserInfo>
          </UserProfile>

          {isProfileOpen && (
            <ProfilePanel>
              <PanelSection>
                <PanelTitle>내 정보</PanelTitle>
                <InfoGrid>
                  <InfoLabel>이름</InfoLabel>
                  <InfoText title={user?.username || '-'}>{user?.username || '-'}</InfoText>
                  <InfoLabel>로그인 ID</InfoLabel>
                  <InfoText title={user?.loginId || '-'}>{user?.loginId || '-'}</InfoText>
                  <InfoLabel>조직</InfoLabel>
                  <InfoText title={user?.groupDisplayName || user?.groupName || '-'}>
                    {user?.groupDisplayName || user?.groupName || '-'}
                  </InfoText>
                  <InfoLabel>역할</InfoLabel>
                  <InfoText title={user?.roleName || '-'}>
                    {user?.roleName || '-'}
                  </InfoText>
                  <InfoLabel>내선번호</InfoLabel>
                  <InfoText title={user?.phone || '-'}>
                    {user?.phone || '-'}
                  </InfoText>
                </InfoGrid>
                <ModuleSection aria-labelledby="current-module-label">
                  <ModuleSectionTitle id="current-module-label">현재 모듈</ModuleSectionTitle>
                  {hasBothConsoleModes ? (
                    <ModeSwitch role="group" aria-label="콘솔 모드">
                      <ModeButton
                        type="button"
                        $mode="basic"
                        $active={mode === 'basic'}
                        aria-pressed={mode === 'basic'}
                        onClick={() => handleModeSwitch('basic')}
                      >
                        {MODE_LABELS.basic}
                      </ModeButton>
                      <ModeButton
                        type="button"
                        $mode="core"
                        $active={mode === 'core'}
                        aria-pressed={mode === 'core'}
                        onClick={() => handleModeSwitch('core')}
                      >
                        {MODE_LABELS.core}
                      </ModeButton>
                    </ModeSwitch>
                  ) : (
                    <ModeValue $mode={mode}>{MODE_LABELS[mode]}</ModeValue>
                  )}
                </ModuleSection>
              </PanelSection>

              <PanelDivider />

              <PanelSection>
                <PanelTitle>비밀번호 변경</PanelTitle>
                <PasswordForm onSubmit={handlePasswordSubmit}>
                  <FieldGroup>
                    <FieldLabel htmlFor="profile-current-password">현재 비밀번호</FieldLabel>
                    <PasswordInput
                      type="password"
                      id="profile-current-password"
                      name="currentPassword"
                      autoComplete="current-password"
                      value={passwordForm.currentPassword}
                      onChange={handlePasswordChange}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel htmlFor="profile-new-password">새 비밀번호</FieldLabel>
                    <PasswordInput
                      type="password"
                      id="profile-new-password"
                      name="newPassword"
                      autoComplete="new-password"
                      value={passwordForm.newPassword}
                      onChange={handlePasswordChange}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel htmlFor="profile-confirm-password">새 비밀번호 확인</FieldLabel>
                    <PasswordInput
                      type="password"
                      id="profile-confirm-password"
                      name="confirmPassword"
                      autoComplete="new-password"
                      value={passwordForm.confirmPassword}
                      onChange={handlePasswordChange}
                    />
                  </FieldGroup>
                  <PanelActionRow>
                    <SaveButton type="submit" disabled={isChangingPassword}>
                      {isChangingPassword ? '변경 중...' : '변경'}
                    </SaveButton>
                  </PanelActionRow>
                </PasswordForm>
              </PanelSection>
            </ProfilePanel>
          )}
        </ProfileWrapper>
        <LogoutButton type="button" title="로그아웃" aria-label="로그아웃" onClick={logout}>
          <LogoutIcon />
        </LogoutButton>
      </Actions>
    </HeaderContainer>
  );
};

export default Header;
